import { registerSW } from 'virtual:pwa-register';
import { BUILD } from './version.ts';

/**
 * Aktualisierungen der installierten App.
 *
 * Vorher stand der Service Worker auf `autoUpdate`. Der erneuert sich zwar von selbst,
 * prüft aber nur beim Laden der Seite — und eine installierte PWA auf dem Telefon wird
 * nie wirklich geschlossen. Das Ergebnis war eine App, die nur nach dem Löschen und
 * Neuinstallieren neuen Code bekam.
 *
 * Jetzt wird regelmäßig nachgesehen und gefragt, statt im Hintergrund zu tauschen.
 * Ein Austausch braucht ein Neuladen, und Neuladen sperrt den Vault — das darf nicht
 * unangekündigt mitten im Schreiben passieren.
 */

/** Stündlich, und immer wenn die App wieder in den Vordergrund kommt. */
const EVERY = 60 * 60 * 1000;

type Listener = (available: boolean) => void;

let apply: ((reload?: boolean) => Promise<void>) | null = null;
let available = false;
const listeners = new Set<Listener>();

function announce(): void {
  for (const listener of listeners) listener(available);
}

export function onUpdateAvailable(listener: Listener): () => void {
  listeners.add(listener);
  listener(available);
  return () => listeners.delete(listener);
}

/** Wendet die bereitliegende Fassung an. Lädt die Seite neu — der Vault sperrt dabei. */
export async function applyUpdate(): Promise<void> {
  if (!apply) return;
  await apply(true);

  // Das Neuladen hängt daran, dass ein Wechsel des kontrollierenden Workers gemeldet
  // wird. Beim allerersten Besuch kontrolliert noch keiner die Seite, dann bleibt die
  // Meldung aus und der Knopf täte nichts. Also nach kurzer Frist selbst neu laden.
  setTimeout(() => window.location.reload(), 1500);
}

let registrierung: ServiceWorkerRegistration | null = null;

/**
 * Sucht auf Zuruf nach einer neuen Fassung. Gibt zurück, ob eine bereitliegt — die
 * Antwort „schon aktuell" ist genauso wichtig wie die andere.
 */
export async function checkForUpdate(): Promise<boolean> {
  if (!registrierung) return available;
  await registrierung.update().catch(() => undefined);
  // Der Wechsel wird über `onNeedRefresh` gemeldet; kurz warten, dann Bilanz ziehen.
  await new Promise((weiter) => setTimeout(weiter, 1_500));
  return available;
}

export function startUpdateWatch(): void {
  // Sorgt dafür, dass der Stand im Bündel landet und sich zwischen Fassungen ändert.
  console.info(`[athene] Fassung ${BUILD}`);
  apply = registerSW({
    onNeedRefresh() {
      available = true;
      announce();
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      registrierung = registration;
      const look = () => void registration.update().catch(() => undefined);
      setInterval(look, EVERY);
      // Auf dem Telefon liegt die App meist im Hintergrund; der Wechsel dorthin ist
      // der verlässlichste Zeitpunkt zum Nachsehen.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') look();
      });
      look();
    },
  });
}
